import { useTranslation } from 'react-i18next';
import CatalogListHeader from '../../components/CatalogListHeader/CatalogListHeader';
import LoaderOverlay from '../../components/LoaderOverlay/LoaderOverlay';
import { ActionIcon, Button, Card, Flex, Grid, List, TextInput, Tooltip } from '@mantine/core';
import { useLocation } from 'react-router-dom';
import { IconColumns, IconFileSpreadsheet, IconLayoutGrid } from '@tabler/icons-react';
import CatalogContainer from '../../components/CatalogContainer/CatalogContainer';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import useDownloadExcel from '../../hooks/useDownloadExcel/useDownloadExcel';
import { useGetOrders, useGetRequestedOrdersSummary } from '../../hooks/orders/queries';
import { IOrder } from '../../interfaces/Order';
import OrderStatuses from '../../enum/OrderStatuses';
import { useDisclosure } from '@mantine/hooks';
import OrderProductsModal from '../../components/OrderModals/OrderProductsModal';
import { showNotification } from '../../utils/Notification/Notification';
import {
  useSendEmailReceipt,
  useSetOrderAccepted,
  useSetOrderCancelled
} from '../../hooks/orders/mutations';
import { exportOrdersListV2 } from '../../services/order.service';
import { downloadTicketAsPNG, tryPrintTicket } from '../../utils/ticketHelper';
import { useSelector } from 'react-redux';
import { MRT_PaginationState } from 'mantine-react-table';
import RequestedOrderTable from '../../components/RequestedOrdersViews/RequestedOrderTable';
import RequestedOrderCards from '../../components/RequestedOrdersViews/RequestedOrderCards';
import { useGetStores } from '../../hooks/stores/queries';
import { buildOrderFolio } from '../../utils/ordersUtils';

const RequestedOrders = () => {
  const { t } = useTranslation([
    'FieldName',
    'CommonButtons',
    'Notifications',
    'OrdersPage',
    'RequestedOrdersPage',
    'UpdateOrderForm',
    'ReportsPage'
  ]);
  const { company, user } = useSelector((state) => state.user);
  // const allowMultiStoreView = user?.permission['allowMultiStoreView']?.canView;
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const searchTermInputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const orderStatusFilter = OrderStatuses.CREATED;
  const [showTableView, setShowTableView] = useState(false);
  const { data: stores } = useGetStores();
  const storeIds = useMemo(() => {
    // needed this way to prevent refetching of requestedOrderSummaryData when navigating or re-rendering
    if (!stores) return '';
    return stores
      .map((s: any) => s._id)
      .sort()
      .join(','); // ordena para que siempre sea estable
  }, [stores]);
  const { data: requestedOrderSummaryData, refetch: refetchRequestedOrdersSummary } =
    useGetRequestedOrdersSummary(storeIds);

  const setSearchParamsDebounced = debounce((val: string) => {
    setSearchTerm(val || undefined);
  }, 500);
  const queryClient = useQueryClient();
  const { downloadExcelV2, loadingExport } = useDownloadExcel();
  const [openedOrderProductsModal, orderProductsModalhandlers] = useDisclosure(false);
  const [selectedOrder, setSelectedOrder] = useState<IOrder | null>(null);
  const sendEmailReceipt = useSendEmailReceipt();
  const setOrderAccepted = useSetOrderAccepted();
  const setOrderCancelled = useSetOrderCancelled();
  const limitPerReq = 9;
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 9
  });
  const { data, fetchNextPage, hasNextPage, isLoading, isError } = useGetOrders(
    !!searchParamStoreId,
    {
      pageNum: showTableView ? pagination.pageIndex : undefined,
      q: searchTerm,
      status: orderStatusFilter,
      storeId: searchParamStoreId,
      limit: limitPerReq,
      fetchSection: 'true'
    }
  );

  const orders = useMemo(() => {
    return data?.pages?.reduce((list, { orders }: { orders: IOrder[] }) => {
      list.push(...orders);
      return list;
    }, [] as IOrder[]);
  }, [data]);

  const totalRowsCount = useMemo(() => data?.pages[0]?.totalRows[0]?.totalRowsCount || -1, [data]);
  const pages = useMemo(() => Math.ceil(totalRowsCount / 9) || -1, [totalRowsCount]);

  const showOrderProductsModal = (order: IOrder) => {
    setSelectedOrder(order);
    orderProductsModalhandlers.open();
  };

  const onSendEmailReceipt = async (orderId: string) => {
    try {
      if (sendEmailReceipt.isPending) return;
      await sendEmailReceipt.mutateAsync(orderId);
      showNotification({ type: 'Default', title: t('Notifications:success') });
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
    }
  };

  const clearSearchTerm = () => {
    if (searchTermInputRef.current) {
      searchTermInputRef.current.value = ''; // Reset the input field value via ref
      setSearchParamsDebounced(undefined); // Reset the state to trigger re-render
    }
  };

  const onPrintTicket = async (order: IOrder) => {
    try {
      tryPrintTicket(order, order.createdIn, t, 'ticketStructure', company.logoUrl, false, company?.companyTaxInfo);
    } catch (e) {}
  };

  const onDownloadPNGTicket = async (order: IOrder) => {
    try {
      downloadTicketAsPNG(order, order.createdIn, t, 'ticketStructure', company.logoUrl);
    } catch (e) {
      console.log('[ERROR] onDownloadPNGTicket:', e);
    }
  };

  const onOrderAccepted = async ({ sequence, storeId }: { sequence: string; storeId: string }) => {
    try {
      if (setOrderAccepted.isPending) return;
      await setOrderAccepted.mutateAsync({
        sequence: sequence,
        storeId: storeId || ''
      });
      await refetchRequestedOrdersSummary();
      showNotification({ type: 'Default', title: t('Notifications:success') });
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      throw e;
    }
  };

  const onOrderCancelled = async ({ sequence, storeId }: { sequence: string; storeId: string }) => {
    try {
      if (setOrderCancelled.isPending) return;
      await setOrderCancelled.mutateAsync({
        sequence: sequence,
        storeId: storeId,
        data: {
          cancelledDateTime: new Date().toISOString()
        }
      });
      await refetchRequestedOrdersSummary();
      showNotification({ type: 'Default', title: t('Notifications:success') });
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      throw e;
    }
  };

  useEffect(() => {
    return () => {
      queryClient.removeQueries({
        queryKey: ['orders']
      });
    };
  }, []);

  useEffect(() => {
    if (data && selectedOrder) {
      const updatedOrder = orders.find((order) => order._id === selectedOrder._id);
      if (updatedOrder) {
        setSelectedOrder(updatedOrder);
      }
    }
  }, [data, selectedOrder]);

  return (
    <>
      <OrderProductsModal
        title={`${t('OrdersPage:productsModal.title')}: ${buildOrderFolio(
          (selectedOrder as unknown as IOrder)?.folioPrefix,
          (selectedOrder as unknown as IOrder)?.sequence
        )}`}
        close={() => {
          orderProductsModalhandlers.close();
        }}
        opened={openedOrderProductsModal}
        order={selectedOrder}
      />
      <CatalogContainer pos="relative">
        <CatalogListHeader title={t('RequestedOrdersPage:title')} showCreateButton={false} />
        <LoaderOverlay
          visible={isLoading || setOrderAccepted.isPending || setOrderCancelled.isPending}
        />
        <Grid>
          <Grid.Col span={12} my={10}>
            <Grid align="center" justify="center">
              {/* {allowMultiStoreView && ( */}
              <Grid.Col span={{ xs: 12, md: 12 }}>
                <Card>
                  Pedidos Solicitados Por Sucursal:
                  <List>
                    {requestedOrderSummaryData?.map((storeSummary) => {
                      return (
                        <List.Item>
                          {storeSummary.storeName}: {storeSummary.count}
                        </List.Item>
                      );
                    })}
                  </List>
                </Card>
              </Grid.Col>
              {/* )} */}
              <Grid.Col span={{ xs: 12, md: 8 }}>
                <TextInput
                  ref={searchTermInputRef}
                  onChange={(event) => setSearchParamsDebounced(event.currentTarget.value)}
                  placeholder={`${t('FieldName:order')}, ${t('FieldName:customer')}, ${t(
                    'FieldName:phone'
                  )}`}
                  label={`${t('FieldName:order')}`}
                />
              </Grid.Col>
              <Grid.Col span={{ xs: 12, md: 2 }} style={{ alignSelf: 'flex-end' }}>
                <Button fullWidth type="button" color="gray" onClick={clearSearchTerm}>
                  {t('CommonButtons:clear').toUpperCase()}
                </Button>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, md: 2 }} style={{ alignSelf: 'flex-end' }}>
                <Flex justify="space-around">
                  <Tooltip withinPortal={false} label={t('CommonButtons:exportList')}>
                    <ActionIcon
                      disabled={loadingExport}
                      onClick={() => {
                        downloadExcelV2(async () => {
                          try {
                            return await exportOrdersListV2({
                              q: searchTerm,
                              status: orderStatusFilter,
                              storeId: searchParamStoreId
                            });
                          } catch (e) {
                            throw e;
                          }
                        }, `Ordenes_${searchParamStoreId}`);
                      }}
                      variant="filled"
                      color="#1D6F42"
                      aria-label="Descargar_Listado_CSV_Ordenes">
                      <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip withinPortal={false} label="Listado en tarjetas">
                    <ActionIcon
                      disabled={loadingExport}
                      onClick={() => {
                        setShowTableView(false);
                      }}
                      variant="filled"
                      color="black"
                      aria-label="Listado_ordenes_tarjegas">
                      <IconLayoutGrid style={{ width: '70%', height: '70%' }} stroke={1.5} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip withinPortal={false} label="Listado en tabla">
                    <ActionIcon
                      disabled={loadingExport}
                      onClick={() => {
                        setShowTableView(true);
                      }}
                      variant="filled"
                      color="black"
                      aria-label="Listado_ordenes_tabla">
                      <IconColumns style={{ width: '70%', height: '70%' }} stroke={1.5} />
                    </ActionIcon>
                  </Tooltip>
                </Flex>
              </Grid.Col>
            </Grid>
          </Grid.Col>
        </Grid>
        {showTableView && (
          <RequestedOrderTable
            orders={orders}
            totalRowsCount={totalRowsCount}
            pages={pages}
            isError={isError}
            isLoading={isLoading}
            pagination={pagination}
            setPagination={setPagination}
            searchParamStoreId={searchParamStoreId}
            onOrderAccepted={onOrderAccepted}
            onOrderCancelled={onOrderCancelled}
            onSendEmailReceipt={onSendEmailReceipt}
            onPrintTicket={onPrintTicket}
            showOrderProductsModal={showOrderProductsModal}
          />
        )}
        {!showTableView && (
          <RequestedOrderCards
            orders={orders}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            searchParamStoreId={searchParamStoreId}
            onOrderAccepted={onOrderAccepted}
            onOrderCancelled={onOrderCancelled}
            onSendEmailReceipt={onSendEmailReceipt}
            onPrintTicket={onPrintTicket}
            onDownloadPNGTicket={onDownloadPNGTicket}
            showOrderProductsModal={showOrderProductsModal}
          />
        )}
      </CatalogContainer>
    </>
  );
};

export default RequestedOrders;
